import type { ConstantsInboxNotificationStruct } from "@gosaas/types";
import { IconBell, IconChecks, IconInbox, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "sonner";
import { useFetchInbox, useInboxActions } from "@/components/queries/inbox";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { TypedClientApiError } from "@/utils/api";

/** Compact relative timestamp for inbox rows ("2h ago", "3d ago"). */
export function relativeTime(value?: string, now: Date = new Date()): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days}d ago`;
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

/** Badge label for the unread count (capped at 9+). */
export function unreadBadgeLabel(count: number): string {
	if (count <= 0) return "";
	return count > 9 ? "9+" : String(count);
}

function InboxRow({
	notification,
	onRead,
	onRemove,
}: {
	notification: ConstantsInboxNotificationStruct;
	onRead: (id: string) => void;
	onRemove: (id: string) => void;
}) {
	const unread = !notification.read_at;
	return (
		<div
			className={cn(
				"w-full px-3 py-2.5 flex items-start gap-2 hover:bg-muted/50 transition-colors group",
				unread && "bg-primary/5",
			)}
		>
			<button
				type="button"
				className="flex items-start gap-2 text-left min-w-0 flex-1"
				onClick={() => {
					if (unread && notification.id) onRead(notification.id);
				}}
			>
				<span
					className={cn(
						"mt-1.5 size-2 rounded-full shrink-0",
						unread ? "bg-primary" : "bg-transparent",
					)}
					aria-hidden
				/>
				<span className="min-w-0 flex-1">
					<span className="block text-sm font-medium truncate">
						{notification.title}
					</span>
					{notification.body && (
						<span className="block text-xs text-muted-foreground line-clamp-2">
							{notification.body}
						</span>
					)}
					<span className="block text-xs text-muted-foreground/70 mt-0.5">
						{relativeTime(notification.created_at)}
					</span>
				</span>
			</button>
			<button
				type="button"
				aria-label="Dismiss notification"
				className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity p-1 rounded hover:bg-muted shrink-0"
				onClick={() => {
					if (notification.id) onRemove(notification.id);
				}}
			>
				<IconX className="size-3.5 text-muted-foreground" aria-hidden />
			</button>
		</div>
	);
}

export function InboxBell() {
	const [open, setOpen] = useState(false);
	const { inbox, isLoadingInbox } = useFetchInbox();
	const { markRead, markAllRead, remove } = useInboxActions();

	const unreadCount = inbox?.unread_count ?? 0;
	const notifications = inbox?.notifications ?? [];

	const handleAction = async (action: () => Promise<void>) => {
		try {
			await action();
		} catch (error) {
			toast.error("Couldn't update notifications", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				className="focus-visible:border-ring focus-visible:ring-ring/50 rounded-lg border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none select-none hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground size-7 p-0 relative"
				title="Notifications"
				aria-label={
					unreadCount > 0
						? `Notifications (${unreadCount} unread)`
						: "Notifications"
				}
			>
				<IconBell />
				{unreadCount > 0 && (
					<span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] leading-4 font-semibold text-center">
						{unreadBadgeLabel(unreadCount)}
					</span>
				)}
			</PopoverTrigger>
			<PopoverContent className="w-96 p-0" align="end" sideOffset={8}>
				<div className="flex items-center justify-between px-3 py-2 border-b">
					<p className="text-sm font-semibold">Notifications</p>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 text-xs"
						disabled={unreadCount === 0}
						onClick={() => handleAction(markAllRead)}
					>
						<IconChecks className="size-3.5 mr-1" aria-hidden />
						Mark all read
					</Button>
				</div>
				<div className="max-h-96 overflow-y-auto divide-y">
					{isLoadingInbox && (
						<p className="px-3 py-6 text-sm text-muted-foreground text-center">
							Loading notifications…
						</p>
					)}
					{!isLoadingInbox && notifications.length === 0 && (
						<div className="px-3 py-8 text-center">
							<IconInbox
								className="size-8 text-muted-foreground/50 mx-auto mb-2"
								aria-hidden
							/>
							<p className="text-sm text-muted-foreground">
								You're all caught up
							</p>
						</div>
					)}
					{notifications.map((notification) => (
						<InboxRow
							key={notification.id}
							notification={notification}
							onRead={(id) => handleAction(() => markRead(id))}
							onRemove={(id) => handleAction(() => remove(id))}
						/>
					))}
				</div>
			</PopoverContent>
		</Popover>
	);
}
